import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheckIcon, ArrowRightIcon, ArrowLeftIcon, CheckCircle2Icon, CircleIcon, LockIcon } from 'lucide-react';
import { ModuleData } from '../data/module1';
import { SectionCard } from './SectionCard';
interface ModuleViewProps {
  moduleData: ModuleData;
  onComplete: () => void;
}
export function ModuleView({
  moduleData,
  onComplete
}: ModuleViewProps) {
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [confirmedSections, setConfirmedSections] = useState<Set<number>>(new Set());
  const [allGuidesCompleted, setAllGuidesCompleted] = useState(false);
  const totalSections = moduleData.sections.length;
  const progress = confirmedSections.size / totalSections * 100;
  const activeSection = moduleData.sections[activeSectionIndex];
  const isLastSection = activeSectionIndex === totalSections - 1;
  const canProceed = confirmedSections.has(activeSectionIndex) || true; // Allow proceeding to confirm
  // Scroll to top when section changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [activeSectionIndex]);
  const handleNext = () => {
    // Mark current as confirmed
    setConfirmedSections(prev => {
      const newSet = new Set(prev);
      newSet.add(activeSectionIndex);
      return newSet;
    });
    if (isLastSection) {
      onComplete();
    } else {
      setActiveSectionIndex(prev => prev + 1);
    }
  };
  const handlePrev = () => {
    if (activeSectionIndex > 0) {
      setActiveSectionIndex(prev => prev - 1);
    }
  };
  const handleNavClick = (index: number) => {
    // Only allow navigating to confirmed sections or the immediate next unconfirmed one
    if (confirmedSections.has(index) || index === Math.max(0, ...Array.from(confirmedSections)) + 1 || index === 0 && confirmedSections.size === 0) {
      setActiveSectionIndex(index);
    }
  };
  return <div className="min-h-screen flex flex-col md:flex-row bg-[#f8f9fa] w-full" data-id="element-63">
      {/* Mobile Header / Nav (Horizontal) */}
      <div className="md:hidden bg-white border-b border-gray-200 sticky top-0 z-20" data-id="element-64">
        <div className="p-4 flex items-center justify-between border-b border-gray-100" data-id="element-65">
          <img src="https://cdn.magicpatterns.com/uploads/8ecn9exgjkQqt4cTmAm7H4/Logo.png" alt="INTIGOO Logo" className="h-8 object-contain" data-id="element-66" />
          <div className="text-xs font-bold text-[#8DC63F] bg-green-50 px-3 py-1 rounded-full" data-id="element-67">
            {Math.round(progress)}%
          </div>
        </div>

        {/* Horizontal scrollable nav for mobile */}
        <div className="flex overflow-x-auto py-3 px-4 space-x-4 hide-scrollbar" data-id="element-68">
          {moduleData.sections.map((section, idx) => {
          const isConfirmed = confirmedSections.has(idx);
          const isActive = idx === activeSectionIndex;
          const isLocked = !isConfirmed && idx > Math.max(0, ...Array.from(confirmedSections)) + 1 && !(idx === 0 && confirmedSections.size === 0);
          return <button key={section.id} onClick={() => handleNavClick(idx)} disabled={isLocked} className={`flex-shrink-0 flex items-center space-x-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${isActive ? 'bg-[#8DC63F] text-white' : isConfirmed ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-gray-50 text-gray-400 border border-gray-200'}`} data-id="element-69">
                {isConfirmed ? <CheckCircle2Icon className="w-4 h-4" data-id="element-70" /> : isActive ? <CircleIcon className="w-4 h-4 fill-current" data-id="element-71" /> : <LockIcon className="w-3 h-3" data-id="element-72" />}
                <span data-id="element-73">
                  {idx + 1}. {section.title.split(' ')[0]}
                </span>
              </button>;
        })}
        </div>
        {/* Mobile Progress Bar */}
        <div className="h-1 w-full bg-gray-100" data-id="element-74">
          <div className="h-full bg-[#8DC63F] transition-all duration-500 ease-out" style={{
          width: `${progress}%`
        }} data-id="element-75" />
        </div>
      </div>

      {/* Desktop Sidebar (Fixed) */}
      <div className="hidden md:flex flex-col w-[280px] lg:w-[320px] bg-white border-r border-gray-200 fixed h-screen z-10" data-id="element-76">
        <div className="p-8 border-b border-gray-100" data-id="element-77">
          <img src="https://cdn.magicpatterns.com/uploads/8ecn9exgjkQqt4cTmAm7H4/Logo.png" alt="INTIGOO Logo" className="h-10 object-contain mb-8" data-id="element-78" />
          <div className="flex items-start space-x-3 mb-4" data-id="element-79">
            <div className="bg-green-100 p-2 rounded-lg flex-shrink-0 mt-1" data-id="element-80">
              <ShieldCheckIcon className="h-5 w-5 text-[#8DC63F]" data-id="element-81" />
            </div>
            <div data-id="element-82">
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-83">
                Modul 1
              </h2>
              <p className="text-sm font-bold text-[#1a1a2e] leading-tight" data-id="element-84">
                {moduleData.title}
              </p>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-4 bg-gray-50 p-3 rounded-lg" data-id="element-85">
            <span className="block font-semibold text-gray-700 mb-1" data-id="element-86">
              Verantwortlich:
            </span>
            {moduleData.responsible}
            <br data-id="element-87" />
            {moduleData.department}
          </div>
        </div>

        {/* Vertical Navigation */}
        <div className="flex-1 overflow-y-auto py-6 px-4" data-id="element-88">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 px-4" data-id="element-89">
            Inhalte ({confirmedSections.size}/{totalSections})
          </h3>
          <ul className="space-y-1" data-id="element-90">
            {moduleData.sections.map((section, idx) => {
            const isConfirmed = confirmedSections.has(idx);
            const isActive = idx === activeSectionIndex;
            // Unlocked if it's confirmed, or if it's the very next one after the last confirmed
            const highestConfirmed = confirmedSections.size > 0 ? Math.max(...Array.from(confirmedSections)) : -1;
            const isUnlocked = isConfirmed || idx === highestConfirmed + 1 || idx === 0;
            const isLocked = !isUnlocked;
            return <li key={section.id} data-id="element-91">
                  <button onClick={() => handleNavClick(idx)} disabled={isLocked} className={`w-full flex items-start text-left px-4 py-3 rounded-xl transition-all ${isActive ? 'bg-green-50' : isLocked ? 'opacity-50 cursor-not-allowed hover:bg-transparent' : 'hover:bg-gray-50 cursor-pointer'}`} data-id="element-92">
                    <div className="mt-0.5 mr-3 flex-shrink-0" data-id="element-93">
                      {isConfirmed ? <CheckCircle2Icon className="w-5 h-5 text-[#8DC63F]" data-id="element-94" /> : isActive ? <div className="w-5 h-5 rounded-full border-2 border-[#8DC63F] flex items-center justify-center" data-id="element-95">
                          <div className="w-2 h-2 rounded-full bg-[#8DC63F]" data-id="element-96" />
                        </div> : isLocked ? <LockIcon className="w-4 h-4 text-gray-300 ml-0.5" data-id="element-97" /> : <CircleIcon className="w-5 h-5 text-gray-300" data-id="element-98" />}
                    </div>
                    <span className={`text-sm ${isActive ? 'font-bold text-[#8DC63F]' : isLocked ? 'text-gray-400' : 'font-medium text-[#4a4a5a]'}`} data-id="element-99">
                      {section.title}
                    </span>
                  </button>
                </li>;
          })}
          </ul>
        </div>

        {/* Vertical Progress */}
        <div className="p-6 border-t border-gray-100 bg-gray-50" data-id="element-100">
          <div className="flex justify-between items-center mb-2" data-id="element-101">
            <span className="text-xs font-bold text-gray-500 uppercase" data-id="element-102">
              Fortschritt
            </span>
            <span className="text-sm font-bold text-[#8DC63F]" data-id="element-103">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-104">
            <div className="h-full bg-[#8DC63F] transition-all duration-500 ease-out" style={{
            width: `${progress}%`
          }} data-id="element-105" />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 md:ml-[280px] lg:ml-[320px] flex flex-col min-h-screen" data-id="element-106">
        <div className="flex-1 p-4 md:p-6 lg:p-8 max-w-none mx-auto w-full" data-id="element-107">
          <AnimatePresence mode="wait" data-id="element-108">
            <SectionCard key={activeSection.id} section={activeSection} onGuidesCompletionChange={allCompleted => setAllGuidesCompleted(allCompleted)} data-id="element-109" />
          </AnimatePresence>
        </div>

        {/* Bottom Action Bar */}
        <div className="sticky bottom-0 bg-white/80 backdrop-blur-md border-t border-gray-200 p-4 md:p-6 z-10" data-id="element-110">
          <div className="max-w-none mx-auto w-full flex items-center justify-between" data-id="element-111">
            <button onClick={handlePrev} disabled={activeSectionIndex === 0} className={`flex items-center px-4 py-3 rounded-lg font-medium transition-colors ${activeSectionIndex === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-[#4a4a5a] hover:bg-gray-100'}`} data-id="element-112">
              <ArrowLeftIcon className="w-5 h-5 mr-2" data-id="element-113" />
              <span className="hidden sm:inline" data-id="element-114">Zurück</span>
            </button>

            <div className="flex items-center gap-3" data-id="element-115">
              <button onClick={handleNext} disabled={!allGuidesCompleted} className={`flex items-center px-6 md:px-8 py-3 md:py-4 rounded-xl font-bold text-lg shadow-md transition-all ${allGuidesCompleted ? 'bg-[#8DC63F] hover:bg-[#7ab036] text-white transform hover:-translate-y-0.5 focus:ring-4 focus:ring-green-200' : 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'}`} data-id="element-116">
                {!allGuidesCompleted ? <>
                    <LockIcon className="w-5 h-5 mr-2" data-id="element-117" />
                    Maßnahmen & Quiz abschließen
                  </> : isLastSection ? <>
                    Zur Unterschrift
                    <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-118" />
                  </> : <>
                    Gelesen & Weiter
                    <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-119" />
                  </>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}